import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { healthAppTranslations, getSupportedLanguages, detectBrowserLanguage } from '../utils/healthAppTranslations';

// Merge old translations with new ones
const translations = {
  en: {
    // App
    appName: 'EmergencyCard Health',
    appTagline: 'Your Digital Health Vault',
    
    // Auth
    welcome: 'Welcome',
    login: 'Login with Face ID',
    register: 'Create Account',
    logout: 'Logout',
    name: 'Full Name',
    email: 'Email Address',
    phone: 'Phone Number',
    scanFace: 'Scan Your Face',
    faceIdDesc: 'Use Face ID to securely access your health vault',
    verifying: 'Verifying...',
    
    // Navigation
    dashboard: 'Dashboard',
    medicalHistory: 'Medical History',
    prescriptions: 'Prescriptions',
    reports: 'Reports',
    diary: 'Health Diary',
    referrals: 'Referrals',
    doctorAccess: 'Doctor Access',
    settings: 'Settings',
    supplements: 'Supplements',
    
    // Dashboard
    healthOverview: 'Health Overview',
    recentActivity: 'Recent Activity',
    quickActions: 'Quick Actions',
    addRecord: 'Add Record',
    shareWithDoctor: 'Share with Doctor',
    viewReports: 'View Reports',
    
    // Medical History
    addCondition: 'Add Condition',
    condition: 'Condition',
    diagnosedDate: 'Diagnosed Date',
    description: 'Description',
    doctorName: 'Doctor Name',
    status: 'Status',
    ongoing: 'Ongoing',
    resolved: 'Resolved',
    
    // Prescriptions
    addPrescription: 'Add Prescription',
    medication: 'Medication',
    dosage: 'Dosage',
    frequency: 'Frequency',
    startDate: 'Start Date',
    endDate: 'End Date',
    notes: 'Notes',
    
    // Reports
    addReport: 'Add Report',
    reportType: 'Report Type',
    reportName: 'Report Name',
    reportDate: 'Report Date',
    labName: 'Lab Name',
    findings: 'Findings',
    bloodTest: 'Blood Test',
    xray: 'X-Ray',
    mri: 'MRI',
    ctScan: 'CT Scan',
    ultrasound: 'Ultrasound',
    other: 'Other',
    
    // Diary
    addEntry: 'Add Entry',
    bloodPressure: 'Blood Pressure',
    foodDiary: 'Food Diary',
    exercise: 'Exercise',
    systolic: 'Systolic',
    diastolic: 'Diastolic',
    pulse: 'Pulse',
    meal: 'Meal',
    calories: 'Calories',
    activityType: 'Activity Type',
    duration: 'Duration (mins)',
    
    // Common
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    download: 'Download',
    exportPDF: 'Export as PDF',
    search: 'Search',
    noData: 'No data available',
    loading: 'Loading...',
    success: 'Success',
    error: 'Error',
    
    // Security
    blockchainSecured: 'Bank-Level Security',
    verifyIntegrity: 'Verify Data',
    blockchainVerified: 'Verified & Secure',
    tamperProof: 'Your data is protected',
    
    // Accessibility
    speakText: 'Read Aloud',
    stopSpeaking: 'Stop',
    language: 'Language',
    switchLanguage: 'Switch Language',
    
    // Reminders
    reminders: 'Reminders',
    medicineReminders: 'Medicine Reminders',
    addReminder: 'Add Reminder',
    
    // Pricing
    pricing: 'Pricing',
    choosePlan: 'Choose Your Plan',
    monthly: 'Monthly',
    annual: 'Annual',
    lifetime: 'Lifetime',
    subscribe: 'Subscribe',
    
    flag: '🇺🇸',
    languageName: 'English',
    ...healthAppTranslations.en,
  },
  hi: {
    appName: 'EmergencyCard Health',
    appTagline: 'आपकी डिजिटल स्वास्थ्य तिजोरी',
    welcome: 'स्वागत है',
    welcomeBack: 'वापस स्वागत है!',
    login: 'लॉगिन करें',
    register: 'खाता बनाएं',
    logout: 'लॉग आउट',
    name: 'पूरा नाम',
    email: 'ईमेल पता',
    phone: 'फ़ोन नंबर',
    dashboard: 'डैशबोर्ड',
    medicalHistory: 'चिकित्सा इतिहास',
    prescriptions: 'दवाइयाँ',
    reports: 'रिपोर्ट',
    diary: 'स्वास्थ्य डायरी',
    settings: 'सेटिंग्स',
    supplements: 'सप्लीमेंट्स',
    save: 'सहेजें',
    cancel: 'रद्द करें',
    delete: 'हटाएं',
    edit: 'संपादित करें',
    loading: 'लोड हो रहा है...',
    blockchainSecured: 'बैंक-स्तरीय सुरक्षा',
    tamperProof: 'आपका डेटा सुरक्षित है',
    reminders: 'रिमाइंडर',
    pricing: 'मूल्य निर्धारण',
    
    // Additional translations
    healthOverview: 'स्वास्थ्य सारांश',
    quickActions: 'त्वरित कार्य',
    recentActivity: 'हाल की गतिविधि',
    addRecord: 'रिकॉर्ड जोड़ें',
    shareWithDoctor: 'डॉक्टर से साझा करें',
    viewReports: 'रिपोर्ट देखें',
    emergencyCard: 'आपातकालीन कार्ड',
    referrals: 'रेफरल',
    activeAccess: 'सक्रिय एक्सेस',
    
    // Settings page
    profile: 'प्रोफ़ाइल',
    accountRecovery: 'खाता पुनर्प्राप्ति',
    exportData: 'डेटा निर्यात करें',
    language: 'भाषा',
    privacyPolicy: 'गोपनीयता नीति',
    termsOfService: 'सेवा की शर्तें',
    changePin: 'PIN बदलें',
    
    // Vault
    secureVault: 'सुरक्षित तिजोरी',
    unlockVault: 'तिजोरी खोलें',
    createPin: 'अपना PIN बनाएं',
    enterPin: 'अपना 6-अंकों का PIN दर्ज करें',
    confirmPin: 'PIN की पुष्टि करें',
    
    // Health terms
    bloodPressure: 'रक्तचाप',
    bloodSugar: 'रक्त शर्करा',
    weight: 'वजन',
    temperature: 'तापमान',
    heartRate: 'हृदय गति',
    bloodGroup: 'रक्त समूह',
    allergies: 'एलर्जी',
    medications: 'दवाइयाँ',
    
    // Common
    add: 'जोड़ें',
    submit: 'जमा करें',
    confirm: 'पुष्टि करें',
    back: 'वापस',
    next: 'आगे',
    done: 'हो गया',
    close: 'बंद करें',
    search: 'खोजें',
    today: 'आज',
    yesterday: 'कल',
    
    // Family
    familyMembers: 'परिवार के सदस्य',
    addFamilyMember: 'सदस्य जोड़ें',
    
    // Aayu
    askAayu: 'आयु से पूछें',
    typeMessage: 'अपना संदेश लिखें...',
    
    flag: '🇮🇳',
    languageName: 'हिंदी',
    ...healthAppTranslations.hi,
  },
  es: {
    appName: 'EmergencyCard Health',
    appTagline: 'Tu Bóveda de Salud Digital',
    welcome: 'Bienvenido',
    logout: 'Cerrar Sesión',
    dashboard: 'Panel',
    medicalHistory: 'Historial Médico',
    prescriptions: 'Recetas',
    reports: 'Informes',
    diary: 'Diario de Salud',
    settings: 'Configuración',
    supplements: 'Suplementos',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    edit: 'Editar',
    loading: 'Cargando...',
    blockchainSecured: 'Seguridad Blockchain',
    tamperProof: 'Datos a prueba de manipulación',
    reminders: 'Recordatorios',
    pricing: 'Precios',
    flag: '🇪🇸',
    languageName: 'Español',
    ...healthAppTranslations.es,
  },
  fr: {
    appName: 'EmergencyCard Health',
    appTagline: 'Votre Coffre Santé Numérique',
    welcome: 'Bienvenue',
    logout: 'Déconnexion',
    dashboard: 'Tableau de bord',
    medicalHistory: 'Historique Médical',
    prescriptions: 'Ordonnances',
    reports: 'Rapports',
    diary: 'Journal de Santé',
    settings: 'Paramètres',
    supplements: 'Suppléments',
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    loading: 'Chargement...',
    blockchainSecured: 'Sécurisé par Blockchain',
    tamperProof: 'Données inviolables',
    reminders: 'Rappels',
    pricing: 'Tarifs',
    flag: '🇫🇷',
    languageName: 'Français',
    ...healthAppTranslations.fr,
  },
  de: {
    appName: 'EmergencyCard Health',
    appTagline: 'Ihr Digitaler Gesundheitstresor',
    welcome: 'Willkommen',
    logout: 'Abmelden',
    dashboard: 'Dashboard',
    medicalHistory: 'Krankengeschichte',
    prescriptions: 'Rezepte',
    reports: 'Berichte',
    diary: 'Gesundheitstagebuch',
    settings: 'Einstellungen',
    supplements: 'Nahrungsergänzung',
    save: 'Speichern',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    edit: 'Bearbeiten',
    loading: 'Laden...',
    blockchainSecured: 'Blockchain-gesichert',
    tamperProof: 'Manipulationssichere Daten',
    reminders: 'Erinnerungen',
    pricing: 'Preise',
    flag: '🇩🇪',
    languageName: 'Deutsch',
    ...healthAppTranslations.de,
  },
  pt: {
    appName: 'EmergencyCard Health',
    appTagline: 'Seu Cofre de Saúde Digital',
    welcome: 'Bem-vindo',
    logout: 'Sair',
    dashboard: 'Painel',
    medicalHistory: 'Histórico Médico',
    prescriptions: 'Receitas',
    reports: 'Relatórios',
    diary: 'Diário de Saúde',
    settings: 'Configurações',
    supplements: 'Suplementos',
    save: 'Salvar',
    cancel: 'Cancelar',
    delete: 'Excluir',
    edit: 'Editar',
    loading: 'Carregando...',
    blockchainSecured: 'Protegido por Blockchain',
    tamperProof: 'Dados à prova de adulteração',
    reminders: 'Lembretes',
    pricing: 'Preços',
    flag: '🇧🇷',
    languageName: 'Português',
    ...healthAppTranslations.pt,
  },
  zh: {
    appName: 'EmergencyCard Health',
    appTagline: '您的数字健康保险库',
    welcome: '欢迎',
    logout: '登出',
    dashboard: '仪表板',
    medicalHistory: '病历',
    prescriptions: '处方',
    reports: '报告',
    diary: '健康日记',
    settings: '设置',
    supplements: '补充剂',
    save: '保存',
    cancel: '取消',
    delete: '删除',
    edit: '编辑',
    loading: '加载中...',
    blockchainSecured: '区块链安全',
    tamperProof: '防篡改数据',
    reminders: '提醒',
    pricing: '价格',
    flag: '🇨🇳',
    languageName: '中文',
    ...healthAppTranslations.zh,
  },
  ja: {
    appName: 'EmergencyCard Health',
    appTagline: 'あなたのデジタル健康金庫',
    welcome: 'ようこそ',
    logout: 'ログアウト',
    dashboard: 'ダッシュボード',
    medicalHistory: '病歴',
    prescriptions: '処方箋',
    reports: 'レポート',
    diary: '健康日記',
    settings: '設定',
    supplements: 'サプリメント',
    save: '保存',
    cancel: 'キャンセル',
    delete: '削除',
    edit: '編集',
    loading: '読み込み中...',
    blockchainSecured: 'ブロックチェーン保護',
    tamperProof: '改ざん防止データ',
    reminders: 'リマインダー',
    pricing: '価格',
    flag: '🇯🇵',
    languageName: '日本語',
    ...healthAppTranslations.ja,
  },
  ko: {
    appName: 'EmergencyCard Health',
    appTagline: '디지털 건강 금고',
    welcome: '환영합니다',
    logout: '로그아웃',
    dashboard: '대시보드',
    medicalHistory: '병력',
    prescriptions: '처방전',
    reports: '보고서',
    diary: '건강 일기',
    settings: '설정',
    supplements: '보충제',
    save: '저장',
    cancel: '취소',
    delete: '삭제',
    edit: '편집',
    loading: '로딩 중...',
    blockchainSecured: '블록체인 보안',
    tamperProof: '위변조 방지 데이터',
    reminders: '알림',
    pricing: '가격',
    flag: '🇰🇷',
    languageName: '한국어',
    ...healthAppTranslations.ko,
  },
  ar: {
    appName: 'EmergencyCard Health',
    appTagline: 'خزنة صحتك الرقمية',
    welcome: 'مرحباً',
    logout: 'تسجيل الخروج',
    dashboard: 'لوحة التحكم',
    medicalHistory: 'التاريخ الطبي',
    prescriptions: 'الوصفات الطبية',
    reports: 'التقارير',
    diary: 'يوميات الصحة',
    settings: 'الإعدادات',
    supplements: 'المكملات',
    save: 'حفظ',
    cancel: 'إلغاء',
    delete: 'حذف',
    edit: 'تعديل',
    loading: 'جاري التحميل...',
    blockchainSecured: 'مؤمن بالبلوكتشين',
    tamperProof: 'بيانات مقاومة للتلاعب',
    reminders: 'التذكيرات',
    pricing: 'الأسعار',
    flag: '🇸🇦',
    languageName: 'العربية',
    direction: 'rtl',
    ...healthAppTranslations.ar,
  },
  ru: {
    appName: 'EmergencyCard Health',
    appTagline: 'Ваше цифровое хранилище здоровья',
    welcome: 'Добро пожаловать',
    logout: 'Выйти',
    dashboard: 'Панель',
    medicalHistory: 'История болезни',
    prescriptions: 'Рецепты',
    reports: 'Отчёты',
    diary: 'Дневник здоровья',
    settings: 'Настройки',
    supplements: 'Добавки',
    save: 'Сохранить',
    cancel: 'Отмена',
    delete: 'Удалить',
    edit: 'Редактировать',
    loading: 'Загрузка...',
    blockchainSecured: 'Защита блокчейном',
    tamperProof: 'Защищённые данные',
    reminders: 'Напоминания',
    pricing: 'Цены',
    flag: '🇷🇺',
    languageName: 'Русский',
    ...healthAppTranslations.ru,
  },
};

// All supported languages (available in Settings for full selection)
const allSupportedLanguages = Object.entries(translations).map(([code, data]) => ({
  code,
  name: data.languageName || data.name || code,
  flag: data.flag || '🌐',
  direction: data.direction || 'ltr',
}));

// Primary languages for quick toggle (English and Hindi)
const primaryLanguages = ['en', 'hi'];

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('emergencyCardHealth_language');
    if (saved && translations[saved]) return saved;
    return detectBrowserLanguage();
  });

  useEffect(() => {
    localStorage.setItem('emergencyCardHealth_language', language);
    // Set document direction for RTL languages
    document.documentElement.dir = translations[language]?.direction || 'ltr';
  }, [language]);

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  }, [language]);

  const toggleLanguage = useCallback(() => {
    // Quick toggle between English and Hindi only
    setLanguage(language === 'en' ? 'hi' : 'en');
  }, [language]);

  // Set any language (for full language selector in Settings)
  const selectLanguage = useCallback((langCode) => {
    if (translations[langCode]) {
      setLanguage(langCode);
    }
  }, []);

  // Text-to-Speech using Web Speech API
  const speak = useCallback((text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      // Map language codes to speech synthesis language codes
      const langMap = {
        en: 'en-US', hi: 'hi-IN', es: 'es-ES', fr: 'fr-FR', de: 'de-DE',
        pt: 'pt-BR', zh: 'zh-CN', ja: 'ja-JP', ko: 'ko-KR', ar: 'ar-SA', ru: 'ru-RU'
      };
      utterance.lang = langMap[language] || 'en-US';
      utterance.rate = 0.9;
      window.speechSynthesis.speak(utterance);
    }
  }, [language]);

  const stopSpeaking = useCallback(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }, []);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage: selectLanguage,  // Use selectLanguage for explicit language changes
      t, 
      toggleLanguage,  // Quick toggle for English ↔ Hindi
      speak, 
      stopSpeaking,
      translations,
      supportedLanguages: allSupportedLanguages,  // All languages available
      primaryLanguages,  // English and Hindi for quick toggle
      currentLanguageInfo: {
        code: language,
        name: translations[language]?.languageName || translations[language]?.name,
        flag: translations[language]?.flag,
        direction: translations[language]?.direction || 'ltr',
      }
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
